import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

export default function AdminSupport() {
  const [knownIssues, setKnownIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddIssue, setShowAddIssue] = useState(false);
  const [showUserLookup, setShowUserLookup] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userNotes, setUserNotes] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);

  // Form states
  const [issueTitle, setIssueTitle] = useState("");
  const [issueDescription, setIssueDescription] = useState("");
  const [issueSeverity, setIssueSeverity] = useState("medium");
  const [issueStatus, setIssueStatus] = useState("open");
  const [noteText, setNoteText] = useState("");
  const [noteCategory, setNoteCategory] = useState("general");

  useEffect(() => {
    fetchKnownIssues();
  }, []);

  const fetchKnownIssues = async () => {
    const token = localStorage.getItem("admin_token");
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/support/issues`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setKnownIssues(response.data.issues || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUserSearch = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    const token = localStorage.getItem("admin_token");
    try {
      const response = await axios.get(`${API}/admin/support/quick-lookup?query=${query}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSearchResults(response.data.users || []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSelectUser = async (user) => {
    setSelectedUser(user);
    setSearchResults([]);
    setShowUserLookup(false);

    // Fetch user notes
    const token = localStorage.getItem("admin_token");
    try {
      const response = await axios.get(`${API}/admin/support/notes/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUserNotes(response.data.notes || []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleAddIssue = async () => {
    if (!issueTitle || !issueDescription) {
      toast.error("Please fill all fields");
      return;
    }

    const token = localStorage.getItem("admin_token");
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/admin/support/issues`,
        {
          title: issueTitle,
          description: issueDescription,
          severity: issueSeverity,
          status: issueStatus,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Known issue logged");
      setShowAddIssue(false);
      setIssueTitle("");
      setIssueDescription("");
      fetchKnownIssues();
    } catch (error) {
      toast.error("Failed to add issue");
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteText || !selectedUser) {
      toast.error("Please enter a note");
      return;
    }

    const token = localStorage.getItem("admin_token");
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/admin/support/notes`,
        {
          user_id: selectedUser.id,
          note: noteText,
          category: noteCategory,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Note added");
      setShowAddNote(false);
      setNoteText("");
      handleSelectUser(selectedUser); // Refresh notes
    } catch (error) {
      toast.error("Failed to add note");
    } finally {
      setActionLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      low: "bg-blue-600",
      medium: "bg-yellow-600",
      high: "bg-orange-600",
      critical: "bg-red-600",
    };
    return colors[severity] || "bg-slate-600";
  };

  const getStatusColor = (status) => {
    const colors = {
      open: "bg-red-600",
      investigating: "bg-yellow-600",
      resolved: "bg-green-600",
    };
    return colors[status] || "bg-slate-600";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Support Tools</h1>
          <p className="text-muted-foreground mt-1">Customer support and incident management</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowUserLookup(true)}
            className="px-4 py-2 bg-teal-600 hover:bg-primary/90 text-foreground rounded-lg transition"
          >
            🔍 User Lookup
          </button>
          <button
            onClick={() => setShowAddIssue(true)}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-foreground rounded-lg transition"
          >
            + Log Known Issue
          </button>
        </div>
      </div>

      {/* User Info Card (if selected) */}
      {selectedUser && (
        <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-foreground">{selectedUser.name}</h2>
              <p className="text-muted-foreground">{selectedUser.email}</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowAddNote(true)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-foreground text-sm rounded-lg transition"
              >
                + Add Note
              </button>
              <button
                onClick={() => setSelectedUser(null)}
                className="px-4 py-2 bg-muted hover:bg-slate-600 text-foreground text-sm rounded-lg transition"
              >
                Clear
              </button>
            </div>
          </div>

          {/* User Stats */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-muted p-3 rounded-lg">
              <p className="text-muted-foreground text-xs">Plan</p>
              <p className="text-foreground font-semibold capitalize">{selectedUser.plan_type}</p>
            </div>
            <div className="bg-muted p-3 rounded-lg">
              <p className="text-muted-foreground text-xs">Credits</p>
              <p className="text-foreground font-semibold">{selectedUser.credits_remaining}</p>
            </div>
            <div className="bg-muted p-3 rounded-lg">
              <p className="text-muted-foreground text-xs">Status</p>
              <p className="text-foreground font-semibold capitalize">{selectedUser.subscription_status}</p>
            </div>
            <div className="bg-muted p-3 rounded-lg">
              <p className="text-muted-foreground text-xs">Verified</p>
              <p className="text-foreground font-semibold">{selectedUser.email_verified ? "Yes" : "No"}</p>
            </div>
          </div>

          {/* Support Notes */}
          <div>
            <h3 className="text-foreground font-semibold mb-3">Support Notes ({userNotes.length})</h3>
            {userNotes.length === 0 ? (
              <p className="text-muted-foreground text-sm">No notes yet</p>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {userNotes.map((note) => (
                  <div key={note.id} className="p-3 bg-muted rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <span className="px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded capitalize">
                        {note.category}
                      </span>
                      <span className="text-muted-foreground text-xs">
                        {new Date(note.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-foreground text-sm">{note.note}</p>
                    <p className="text-muted-foreground text-xs mt-1">By: {note.created_by_email}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Known Issues */}
      <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-foreground">Known Issues</h2>
          <span className="px-3 py-1 bg-red-500/10 border border-red-500/20 rounded-full text-red-400 text-sm">
            {knownIssues.filter(i => i.status !== 'resolved').length} open
          </span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
          </div>
        ) : knownIssues.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-green-500/10 border border-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-muted-foreground">No known issues - platform running smoothly!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {knownIssues.map((issue) => (
              <div key={issue.id} className="p-4 bg-muted/50 border border-border rounded-lg">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-foreground font-medium">{issue.title}</h3>
                  <div className="flex gap-2">
                    <span className={`px-2 py-1 ${getSeverityColor(issue.severity)} text-foreground text-xs rounded capitalize`}>
                      {issue.severity}
                    </span>
                    <span className={`px-2 py-1 ${getStatusColor(issue.status)} text-foreground text-xs rounded capitalize`}>
                      {issue.status}
                    </span>
                  </div>
                </div>
                <p className="text-muted-foreground text-sm mb-2">{issue.description}</p>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span>Logged by: {issue.created_by_email}</span>
                  <span>{new Date(issue.created_at).toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* User Lookup Modal */}
      {showUserLookup && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl max-w-lg w-full p-6 space-y-4">
            <h3 className="text-xl font-bold text-foreground">User Lookup</h3>
            <input
              type="text"
              placeholder="Search by email or name..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                handleUserSearch(e.target.value);
              }}
              className="w-full px-4 py-2 bg-muted border border-border rounded-lg text-foreground placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary"
            />

            {searchResults.length > 0 && (
              <div className="bg-muted border border-border rounded-lg max-h-60 overflow-y-auto">
                {searchResults.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => handleSelectUser(user)}
                    className="w-full p-3 text-left hover:bg-muted transition border-b border-border last:border-0"
                  >
                    <p className="text-foreground font-medium">{user.name}</p>
                    <p className="text-muted-foreground text-sm">{user.email}</p>
                    <div className="flex gap-2 mt-1">
                      <span className="text-xs text-muted-foreground capitalize">{user.plan_type}</span>
                      <span className="text-xs text-muted-foreground">{user.credits_remaining} credits</span>
                    </div>
                  </button>
                ))}
              </div>
            )}

            <button
              onClick={() => {
                setShowUserLookup(false);
                setSearchQuery("");
                setSearchResults([]);
              }}
              className="w-full px-4 py-2 bg-muted hover:bg-slate-600 text-foreground rounded-lg transition"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Add Known Issue Modal */}
      {showAddIssue && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl max-w-lg w-full p-6 space-y-4">
            <h3 className="text-xl font-bold text-foreground">Log Known Issue</h3>
            <input
              type="text"
              placeholder="Issue title"
              value={issueTitle}
              onChange={(e) => setIssueTitle(e.target.value)}
              className="w-full px-4 py-2 bg-muted border border-border rounded-lg text-foreground placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <textarea
              placeholder="Issue description"
              value={issueDescription}
              onChange={(e) => setIssueDescription(e.target.value)}
              rows={4}
              className="w-full px-4 py-2 bg-muted border border-border rounded-lg text-foreground placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <div className="grid grid-cols-2 gap-4">
              <select
                value={issueSeverity}
                onChange={(e) => setIssueSeverity(e.target.value)}
                className="px-4 py-2 bg-muted border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="low">Low Severity</option>
                <option value="medium">Medium Severity</option>
                <option value="high">High Severity</option>
                <option value="critical">Critical</option>
              </select>
              <select
                value={issueStatus}
                onChange={(e) => setIssueStatus(e.target.value)}
                className="px-4 py-2 bg-muted border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="open">Open</option>
                <option value="investigating">Investigating</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleAddIssue}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-foreground rounded-lg transition"
              >
                {actionLoading ? "Adding..." : "Log Issue"}
              </button>
              <button
                onClick={() => setShowAddIssue(false)}
                className="flex-1 px-4 py-2 bg-muted hover:bg-slate-600 text-foreground rounded-lg transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Note Modal */}
      {showAddNote && selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl max-w-lg w-full p-6 space-y-4">
            <h3 className="text-xl font-bold text-foreground">Add Support Note for {selectedUser.name}</h3>
            <textarea
              placeholder="Enter support note..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={4}
              className="w-full px-4 py-2 bg-muted border border-border rounded-lg text-foreground placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <select
              value={noteCategory}
              onChange={(e) => setNoteCategory(e.target.value)}
              className="w-full px-4 py-2 bg-muted border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="general">General</option>
              <option value="billing">Billing</option>
              <option value="technical">Technical</option>
              <option value="feedback">Feedback</option>
            </select>
            <div className="flex gap-3">
              <button
                onClick={handleAddNote}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-foreground rounded-lg transition"
              >
                {actionLoading ? "Adding..." : "Add Note"}
              </button>
              <button
                onClick={() => setShowAddNote(false)}
                className="flex-1 px-4 py-2 bg-muted hover:bg-slate-600 text-foreground rounded-lg transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
